/**
 * PDF Helpers - Funciones auxiliares para generación de PDFs con jsPDF
 * 
 * Este módulo proporciona funciones helper para generar PDFs con texto nativo
 * usando jsPDF y jspdf-autotable, resolviendo problemas de paginación.
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

/**
 * Convierte HTML a texto plano preservando estructura
 * 
 * @param {string} html - Contenido HTML
 * @returns {Array<Object>} Array de bloques de texto con formato
 * 
 * Ejemplo de retorno:
 * [
 *   { texto: "Título", tipo: "titulo", negrita: true, tamanoFuente: 14 },
 *   { texto: "Párrafo normal", tipo: "parrafo", negrita: false, tamanoFuente: 10 }
 * ]
 */
export function convertirHtmlATextoPlano(html) {
  try {
    // 1. Crear un elemento DOM temporal
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // 2. Array para almacenar bloques de texto
    const bloques = [];
    
    // 3. Función recursiva para procesar nodos
    function procesarNodo(nodo, contexto = {}) {
      // 3.1 Si es nodo de texto
      if (nodo.nodeType === Node.TEXT_NODE) {
        const texto = nodo.textContent.trim();
        if (texto.length > 0) {
          bloques.push({
            texto: contexto.prefijo ? contexto.prefijo + texto : texto,
            tipo: contexto.tipo || 'parrafo',
            negrita: contexto.negrita || false,
            tamanoFuente: contexto.tamanoFuente || 10,
            color: contexto.color || '#000000',
            indentacion: contexto.indentacion || 0
          });
        }
        return;
      }
      
      // 3.2 Si es nodo elemento
      if (nodo.nodeType === Node.ELEMENT_NODE) {
        const nuevoContexto = { ...contexto };
        
        // Detectar tipo de elemento y ajustar contexto
        const tagName = nodo.tagName.toLowerCase();
        
        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            nuevoContexto.tipo = 'titulo';
            nuevoContexto.negrita = true;
            nuevoContexto.tamanoFuente = 14;
            break;
            
          case 'strong':
          case 'b':
            nuevoContexto.negrita = true;
            break;
            
          case 'p':
            nuevoContexto.tipo = 'parrafo';
            break;
            
          case 'ul':
          case 'ol':
            nuevoContexto.tipo = 'lista';
            nuevoContexto.indentacion = 5;
            break;
            
          case 'li':
            // Agregar viñeta
            nuevoContexto.prefijo = '• ';
            break;
            
          case 'br':
            bloques.push({
              texto: '',
              tipo: 'salto',
              negrita: false,
              tamanoFuente: 10
            });
            return;
            
          case 'div':
          case 'span':
            // Extraer estilos inline si existen
            if (nodo.style.fontWeight === 'bold' || nodo.style.fontWeight === '700') {
              nuevoContexto.negrita = true;
            }
            if (nodo.style.color) {
              nuevoContexto.color = nodo.style.color;
            }
            break;
            
          default:
            // Otros tags no procesados específicamente
            break;
        }
        
        // Procesar hijos recursivamente
        for (const hijo of nodo.childNodes) {
          procesarNodo(hijo, nuevoContexto);
        }
        
        // Agregar salto después de ciertos elementos
        if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tagName)) {
          bloques.push({
            texto: '',
            tipo: 'salto',
            negrita: false,
            tamanoFuente: 5
          });
        }
      }
    }
    
    // 4. Iniciar procesamiento desde la raíz
    for (const hijo of tempDiv.childNodes) {
      procesarNodo(hijo);
    }
    
    // 5. Limpiar bloques vacíos consecutivos
    const bloquesLimpios = [];
    let ultimoFueSalto = false;
    
    for (const bloque of bloques) {
      if (bloque.tipo === 'salto') {
        if (!ultimoFueSalto) {
          bloquesLimpios.push(bloque);
          ultimoFueSalto = true;
        }
      } else {
        bloquesLimpios.push(bloque);
        ultimoFueSalto = false;
      }
    }
    
    // 6. Retornar bloques procesados
    return bloquesLimpios;
    
  } catch (error) {
    console.error('Error convirtiendo HTML a texto:', error);
    // Fallback: retornar texto plano sin formato
    return [{
      texto: html.replace(/<[^>]*>/g, ''),  // Eliminar tags
      tipo: 'parrafo',
      negrita: false,
      tamanoFuente: 10,
      color: '#000000',
      indentacion: 0
    }];
  }
}

/**
 * Convierte el encabezado HTML a imagen y la agrega al PDF
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Object} encabezado - Objeto con {izquierda, centro, derecha}
 * @param {number} yPosition - Posición Y donde colocar el encabezado
 * @returns {Promise<number>} Nueva posición Y después del encabezado
 */
export async function procesarEncabezadoComoImagen(doc, encabezado, yPosition) {
  // 1. Crear elemento HTML temporal con el encabezado
  const tempDiv = document.createElement('div');
  tempDiv.style.width = '190mm';  // Ancho de página menos márgenes
  tempDiv.style.padding = '10px';
  tempDiv.style.backgroundColor = 'white';
  tempDiv.style.display = 'flex';
  tempDiv.style.justifyContent = 'space-between';
  
  // 2. Agregar las tres columnas
  tempDiv.innerHTML = `
    <div style="flex: 1; text-align: left;">
      ${encabezado.izquierda || ''}
    </div>
    <div style="flex: 1; text-align: center;">
      ${encabezado.centro || ''}
    </div>
    <div style="flex: 1; text-align: right;">
      ${encabezado.derecha || ''}
    </div>
  `;
  
  // 3. Agregar temporalmente al DOM (necesario para html2canvas)
  tempDiv.style.position = 'absolute';
  tempDiv.style.left = '-9999px';
  document.body.appendChild(tempDiv);
  
  // 4. Convertir a canvas usando html2canvas
  try {
    const canvas = await html2canvas(tempDiv, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff'
    });
    
    // 5. Convertir canvas a imagen base64
    const imagenBase64 = canvas.toDataURL('image/jpeg', 0.95);
    
    // 6. Calcular dimensiones para el PDF
    const anchoImagen = 190;  // mm
    const altoImagen = (canvas.height / canvas.width) * anchoImagen;
    
    // 7. Agregar imagen al PDF
    doc.addImage(
      imagenBase64,
      'JPEG',
      10,  // x (margen izquierdo)
      yPosition,
      anchoImagen,
      altoImagen
    );
    
    // 8. Limpiar elemento temporal
    document.body.removeChild(tempDiv);
    
    // 9. Retornar nueva posición Y
    return yPosition + altoImagen + 3;
    
  } catch (error) {
    console.error('Error procesando encabezado:', error);
    document.body.removeChild(tempDiv);
    
    // Fallback: renderizar como texto simple
    doc.setFontSize(10);
    doc.text('Encabezado no disponible', 10, yPosition);
    return yPosition + 10;
  }
}

/**
 * Agrega una firma como imagen al PDF
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {string} imagenBase64 - Imagen en formato base64
 * @param {number} yPosition - Posición Y donde colocar la firma
 * @param {Object} metadata - Metadatos de la firma (nombre, RM, etc.)
 * @returns {number} Nueva posición Y después de la firma
 */
export function agregarFirmaComoImagen(doc, imagenBase64, yPosition, metadata = {}) {
  // 1. Crear tabla para la firma
  const filas = [];
  
  // 2. Determinar título según tipo
  const titulo = metadata.tipo === 'profesional' ? 'FIRMA DEL PROFESIONAL' : 'FIRMA DEL PACIENTE';
  
  // 3. Agregar fila de título
  filas.push([titulo]);
  
  // 4. Determinar altura de celda según tipo de firma
  // Profesional: necesita más espacio para metadata (nombre, RM, habilitación)
  // Paciente: solo necesita espacio para la imagen
  const alturaCelda = metadata.tipo === 'profesional' ? 45 : 35;
  
  // 5. Configurar tabla con altura fija
  doc.autoTable({
    body: filas,
    startY: yPosition,
    theme: 'plain',
    headStyles: {
      fillColor: [248, 249, 250],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 10,
      cellPadding: 2
    },
    styles: {
      minCellHeight: alturaCelda,  // Altura mínima fija
      valign: 'top'
    },
    margin: { left: 15, right: 15 },
    tableLineColor: [222, 226, 230],
    tableLineWidth: 0.1,
    didDrawCell: (data) => {
      // Hook para agregar la imagen dentro de la celda
      if (data.row.index === 0 && data.column.index === 0) {
        // Agregar imagen de firma
        try {
          doc.addImage(
            imagenBase64,
            'PNG',
            data.cell.x + 5,
            data.cell.y + 5,
            50,  // ancho
            25   // alto
          );
        } catch (error) {
          console.error('Error agregando firma:', error);
        }
        
        // Agregar metadata debajo de la firma (solo para profesional)
        if (metadata.tipo === 'profesional' && (metadata.nombre || metadata.registroMedico)) {
          let yMetadata = data.cell.y + 32;
          doc.setFontSize(8);
          doc.setTextColor(108, 117, 125);  // Gris
          
          if (metadata.nombre) {
            doc.text(metadata.nombre, data.cell.x + 5, yMetadata);
            yMetadata += 3;
          }
          
          if (metadata.registroMedico) {
            let textoRM = 'RM: ' + metadata.registroMedico;
            if (metadata.codigoHabilitacion) {
              textoRM += ' | Habilitación: ' + metadata.codigoHabilitacion;
            }
            doc.text(textoRM, data.cell.x + 5, yMetadata);
          }
        }
      }
    }
  });
  
  // 6. Retornar nueva posición Y
  return doc.lastAutoTable.finalY;
}

/**
 * Agrega una tabla con bandas grises usando jspdf-autotable
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Array<Array<string>>} filas - Filas de la tabla
 * @param {number} yPosition - Posición Y inicial
 * @param {Object} estilos - Estilos personalizados (opcional)
 * @returns {number} Nueva posición Y después de la tabla
 */
export function agregarTablaConBandas(doc, filas, yPosition, estilos = {}) {
  // 1. Configuración por defecto
  const configDefault = {
    startY: yPosition,
    theme: 'plain',
    headStyles: {
      fillColor: [248, 249, 250],  // Gris claro
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 10,
      cellPadding: 2
    },
    bodyStyles: {
      fontSize: 10,
      cellPadding: 2,
      textColor: [0, 0, 0]
    },
    alternateRowStyles: {
      fillColor: [248, 249, 250]  // Bandas grises
    },
    columnStyles: {
      0: { cellWidth: 60, fontStyle: 'bold', fillColor: [248, 249, 250] },
      1: { cellWidth: 'auto' }
    },
    margin: { left: 15, right: 15 },
    tableLineColor: [222, 226, 230],
    tableLineWidth: 0.1
  };
  
  // 2. Combinar configuración por defecto con estilos personalizados
  const configFinal = { ...configDefault, ...estilos };
  
  // 3. Generar tabla usando jspdf-autotable
  doc.autoTable({
    body: filas,
    ...configFinal
  });
  
  // 4. Obtener posición Y final de la tabla
  const yFinal = doc.lastAutoTable.finalY;
  
  // 5. Retornar nueva posición Y
  return yFinal;
}

/**
 * Verifica si hay espacio suficiente en la página actual
 * Si no hay espacio, crea una nueva página
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {number} yActual - Posición Y actual
 * @param {number} alturaRequerida - Altura del contenido a agregar
 * @param {number} margenInferior - Margen inferior de la página
 * @returns {number} Posición Y ajustada (puede ser en nueva página)
 */
export function aplicarPaginacionInteligente(doc, yActual, alturaRequerida, margenInferior, margenSuperior = 20) {
  // 1. Obtener altura total de la página
  const altoTotal = doc.internal.pageSize.getHeight();
  
  // 2. Calcular espacio disponible en página actual
  const espacioDisponible = altoTotal - yActual - margenInferior;
  
  // 3. Verificar si el contenido cabe en la página actual
  if (alturaRequerida > espacioDisponible) {
    // 3.1 No cabe: crear nueva página
    doc.addPage();
    
    // 3.2 Retornar posición Y inicial de nueva página (alineado con márgenes del documento)
    return margenSuperior;
  } else {
    // 3.3 Sí cabe: mantener posición actual
    return yActual;
  }
}

/**
 * Agrega foliación a todas las páginas del documento
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {number} margenInferior - Margen inferior en mm
 */
export function agregarFoliacion(doc, margenInferior) {
  // 1. Obtener número total de páginas
  const totalPaginas = doc.internal.getNumberOfPages();
  
  // 2. Obtener dimensiones de la página
  const anchoTotal = doc.internal.pageSize.getWidth();
  const altoTotal = doc.internal.pageSize.getHeight();
  
  // 3. Calcular posición Y del folio
  const yFolio = altoTotal - (margenInferior / 2);
  
  // 4. Iterar sobre todas las páginas
  for (let i = 1; i <= totalPaginas; i++) {
    // 4.1 Ir a la página i
    doc.setPage(i);
    
    // 4.2 Configurar estilo del folio
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);  // Gris
    doc.setFont('helvetica', 'normal');
    
    // 4.3 Crear texto del folio
    const textoFolio = `Página ${i} de ${totalPaginas}`;
    
    // 4.4 Agregar texto centrado
    doc.text(
      textoFolio,
      anchoTotal / 2,
      yFolio,
      { align: 'center' }
    );
  }
}

/**
 * Longitud aproximada del texto visible (sin etiquetas HTML).
 * El HTML de editores infla mucho el string y rompía la paginación previa al bloque "texto".
 */
function longitudTextoPlanoAprox(html) {
  if (!html || typeof html !== 'string') return 0;
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length;
}

/**
 * Estima la altura de una sección para paginación
 * 
 * @param {Object} seccion - Sección del documento
 * @returns {number} Altura estimada en mm
 */
export function estimarAlturaSeccion(seccion) {
  // Estimación conservadora de altura para paginación
  
  switch (seccion.tipo) {
    case 'salto-pagina':
      return 0;  // No ocupa espacio, solo crea nueva página
      
    case 'titulo-seccion':
      return 10;  // Altura para títulos de sección
      
    case 'subtitulo':
      return 8;  // Altura para subtítulos
      
    case 'banner':
      return 10;  // Altura fija para banners
      
    case 'tabla':
    case 'tabla-datos':
      const numFilas = seccion.datos ? seccion.datos.filas.length : 1;
      return numFilas * 8 + 5;  // ~8mm por fila + margen
      
    case 'texto': {
      // Usar texto plano, no el HTML crudo (evita saltos de página en blanco).
      // El cuerpo se dibuja línea a línea con paginación real; aquí solo hace falta
      // un tope razonable para decidir si cabe *iniciar* la sección en la página actual.
      const longitudTexto = longitudTextoPlanoAprox(seccion.contenido);
      const lineasEstimadas = Math.max(1, Math.ceil(longitudTexto / 85));
      const alturaMm = lineasEstimadas * 4.2;
      const maxPreChequeo = 55;
      return Math.min(alturaMm, maxPreChequeo);
    }
      
    case 'firma':
    case 'firmas-grupo':
      return 40;  // Altura estimada para firmas
      
    default:
      return 20;  // Valor por defecto conservador
  }
}

/**
 * Genera un PDF con texto nativo usando jsPDF
 * 
 * @param {Object} config - Configuración del documento
 * @param {string} config.filename - Nombre del archivo PDF
 * @param {Object} config.paciente - Datos del paciente
 * @param {string} config.paciente.nombreCompleto - Nombre completo del paciente
 * @param {string} config.paciente.documentoCompleto - Documento del paciente
 * @param {Object|null} config.encabezadoPersonalizado - Encabezado personalizado (opcional)
 * @param {string} config.encabezadoPersonalizado.izquierda - HTML del lado izquierdo
 * @param {string} config.encabezadoPersonalizado.centro - HTML del centro
 * @param {string} config.encabezadoPersonalizado.derecha - HTML del lado derecho
 * @param {Array<Object>} config.secciones - Secciones del documento
 * @param {string} config.secciones[].titulo - Título de la sección
 * @param {string} config.secciones[].contenido - Contenido (puede ser HTML o texto)
 * @param {string} config.secciones[].tipo - Tipo: 'texto', 'tabla', 'banner', 'tabla-datos'
 * @param {Object} config.secciones[].estilos - Estilos específicos (opcional)
 * @param {Array<Object>} config.firmas - Firmas a incluir (opcional)
 * @param {string} config.firmas[].tipo - 'profesional' o 'paciente'
 * @param {string} config.firmas[].imagenBase64 - Imagen de la firma en base64
 * @param {Object} config.firmas[].metadata - Metadatos (nombre, RM, etc.)
 * @param {Object} config.margenes - Márgenes del documento (opcional)
 * @param {number} config.margenes.top - Margen superior en mm (default: 20)
 * @param {number} config.margenes.bottom - Margen inferior en mm (default: 20)
 * @param {number} config.margenes.left - Margen izquierdo en mm (default: 15)
 * @param {number} config.margenes.right - Margen derecho en mm (default: 15)
 * 
 * @returns {Promise<jsPDF>} Objeto jsPDF con el documento generado
 */
export async function generarPdfConTextoNativo(config) {
  // Validación de datos requeridos
  if (!config.paciente) {
    console.error('Datos del paciente no disponibles');
    throw new Error('Datos del paciente son requeridos');
  }
  
  if (!config.secciones || config.secciones.length === 0) {
    console.error('No hay secciones para generar el PDF');
    throw new Error('Se requiere al menos una sección');
  }
  
  // 1. Inicializar documento jsPDF
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });
  
  // 2. Configurar márgenes (con valores por defecto)
  const margenes = config.margenes || {
    top: 20,
    bottom: 20,
    left: 15,
    right: 15
  };
  
  // 3. Calcular dimensiones útiles
  const anchoUtil = doc.internal.pageSize.getWidth() - margenes.left - margenes.right;
  // const altoUtil = doc.internal.pageSize.getHeight() - margenes.top - margenes.bottom; // No usado actualmente
  
  // 4. Posición Y inicial
  let yActual = margenes.top;
  
  // 5. Procesar encabezado personalizado (si existe)
  console.log('🔍 [pdfHelpers] Verificando encabezado personalizado:', !!config.encabezadoPersonalizado);
  if (config.encabezadoPersonalizado) {
    console.log('📋 [pdfHelpers] Datos del encabezado:', config.encabezadoPersonalizado);
    try {
      console.log('🖼️ [pdfHelpers] Procesando encabezado como imagen...');
      yActual = await procesarEncabezadoComoImagen(
        doc, 
        config.encabezadoPersonalizado, 
        yActual
      );
      console.log('✅ [pdfHelpers] Encabezado procesado exitosamente. Nueva posición Y:', yActual);
      yActual += 5;  // Espacio después del encabezado
    } catch (error) {
      console.error('❌ [pdfHelpers] Error procesando encabezado personalizado:', error);
      // Continuar sin encabezado
    }
  }
  
  // 6. Agregar información del paciente (siempre presente)
  yActual = agregarTablaConBandas(
    doc,
    [
      ['PACIENTE', config.paciente.nombreCompleto],
      ['DOCUMENTO', config.paciente.documentoCompleto]
    ],
    yActual,
    {
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold', fillColor: [248, 249, 250] },
        1: { cellWidth: 'auto', fillColor: [255, 255, 255] }
      }
    }
  );
  yActual += 3;  // Espacio entre secciones
  
  // 7. Procesar cada sección del documento
  for (const seccion of config.secciones) {
    // 7.1 Verificar espacio disponible
    const alturaEstimada = estimarAlturaSeccion(seccion);
    yActual = aplicarPaginacionInteligente(
      doc, 
      yActual, 
      alturaEstimada, 
      margenes.bottom,
      margenes.top
    );
    
    // 7.2 Renderizar según tipo de sección
    switch (seccion.tipo) {
      case 'salto-pagina':
        // Salto de página - crear nueva página
        doc.addPage();
        yActual = margenes.top;
        break;
        
      case 'titulo-seccion':
        // Título de sección (más grande que subtítulo)
        doc.setFontSize(seccion.estilos?.fontSize || 14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 94, 104);  // #325e68
        doc.text(seccion.contenido, margenes.left, yActual);
        yActual += 10;  // Más espacio después del título
        break;
        
      case 'subtitulo':
        // Subtítulo (más pequeño que título-seccion)
        doc.setFontSize(seccion.estilos?.fontSize || 12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(50, 94, 104);  // #325e68
        doc.text(seccion.contenido, margenes.left, yActual);
        yActual += 8;  // Más espacio después del subtítulo
        break;
        
      case 'banner':
        // Banner con fondo gris
        doc.setFillColor(233, 236, 239);  // #e9ecef
        doc.rect(margenes.left, yActual, anchoUtil, 8, 'F');
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(seccion.titulo || seccion.contenido, margenes.left + 2, yActual + 5);
        yActual += 10;
        break;
        
      case 'tabla':
        // Tabla simple con bandas
        yActual = agregarTablaConBandas(
          doc,
          seccion.datos ? seccion.datos.filas : [[seccion.titulo, seccion.contenido]],
          yActual,
          seccion.estilos
        );
        // Aire antes del siguiente bloque (evolución pegada al cuadro anterior en PDF)
        yActual += 4;
        break;
        
      case 'tabla-datos':
        // Tabla con etiqueta-valor
        if (seccion.datos && seccion.datos.filas) {
          const filasTabla = seccion.datos.filas.map(fila => [
            fila.etiqueta,
            fila.valor
          ]);
          yActual = agregarTablaConBandas(
            doc,
            filasTabla,
            yActual,
            seccion.estilos
          );
        }
        // Tras FECHA / PROFESIONAL TRATANTE (y tablas similares), dejar espacio claro antes del cuerpo
        yActual += 5;
        break;
        
      case 'texto':
        // Separación explícita: el HTML no aporta margen al pasar por convertirHtmlATextoPlano
        yActual += 5;
        if (seccion.titulo) {
          yActual = aplicarPaginacionInteligente(doc, yActual, 12, margenes.bottom, margenes.top);
          doc.setFontSize(11);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(50, 94, 104);
          doc.text(String(seccion.titulo), margenes.left, yActual);
          yActual += 7;
        }
        // Convertir HTML a texto plano
        const bloques = convertirHtmlATextoPlano(seccion.contenido || '');
        
        // Renderizar cada bloque
        for (const bloque of bloques) {
          if (bloque.tipo === 'salto') {
            yActual += bloque.tamanoFuente * 0.4;
            continue;
          }
          
          // Configurar estilo
          doc.setFontSize(bloque.tamanoFuente || 10);
          doc.setFont('helvetica', bloque.negrita ? 'bold' : 'normal');
          
          if (bloque.color && bloque.color !== '#000000') {
            // Convertir color hex a RGB
            const r = parseInt(bloque.color.slice(1, 3), 16);
            const g = parseInt(bloque.color.slice(3, 5), 16);
            const b = parseInt(bloque.color.slice(5, 7), 16);
            doc.setTextColor(r, g, b);
          } else {
            doc.setTextColor(0, 0, 0);
          }
          
          // Agregar texto con word wrap (paginar antes de cada línea: evita desbordar la página)
          const lineas = doc.splitTextToSize(
            bloque.texto,
            anchoUtil - (bloque.indentacion || 0)
          );
          
          const altoLinea = (bloque.tamanoFuente || 10) * 0.4;
          for (const linea of lineas) {
            yActual = aplicarPaginacionInteligente(
              doc,
              yActual,
              altoLinea + 0.5,
              margenes.bottom,
              margenes.top
            );
            doc.text(
              linea,
              margenes.left + (bloque.indentacion || 0),
              yActual
            );
            yActual += altoLinea;
          }
          
          yActual += 2;  // Espacio entre bloques
        }
        break;
        
      case 'firma':
        // Procesar firma individual como parte de la sección
        try {
          yActual = agregarFirmaComoImagen(
            doc,
            seccion.imagenBase64,
            yActual,
            seccion.metadata || {}
          );
          yActual += 5;  // Espacio después de la firma
        } catch (error) {
          console.error('Error agregando firma en sección:', error);
        }
        break;
        
      case 'firmas-grupo':
        // Procesar múltiples firmas lado a lado
        try {
          yActual = agregarFirmasLadoALado(
            doc,
            seccion.firmas || [],
            yActual
          );
          yActual += 5;  // Espacio después de las firmas
        } catch (error) {
          console.error('Error agregando grupo de firmas:', error);
        }
        break;
        
      default:
        // Tipo de sección no reconocido
        break;
    }
    
    yActual += 3;  // Espacio entre secciones
  }
  
  // 8. Agregar firmas (si existen)
  if (config.firmas && config.firmas.length > 0) {
    // Asegurar que las firmas no se dividan entre páginas
    const alturaFirmas = config.firmas.length * 35;  // Estimación
    yActual = aplicarPaginacionInteligente(
      doc,
      yActual,
      alturaFirmas,
      margenes.bottom,
      margenes.top
    );
    
    for (const firma of config.firmas) {
      try {
        yActual = agregarFirmaComoImagen(
          doc,
          firma.imagenBase64,
          yActual,
          firma.metadata
        );
        yActual += 5;  // Espacio entre firmas
      } catch (error) {
        console.error('Error agregando firma:', error);
        // Continuar con las demás firmas
      }
    }
  }
  
  // 9. Agregar foliación a todas las páginas
  agregarFoliacion(doc, margenes.bottom);
  
  // 10. Retornar documento generado
  return doc;
}


/**
 * Agregar firmas lado a lado (profesional y paciente en la misma fila)
 * 
 * @param {jsPDF} doc - Instancia de jsPDF
 * @param {Array} firmas - Array de objetos de firma con imagenBase64 y metadata
 * @param {number} yPosition - Posición Y inicial
 * @returns {number} Nueva posición Y después de las firmas
 */
export function agregarFirmasLadoALado(doc, firmas, yPosition) {
  if (!firmas || firmas.length === 0) {
    return yPosition;
  }
  
  // Separar firmas por tipo
  const firmaProfesional = firmas.find(f => f.metadata && f.metadata.tipo === 'profesional');
  const firmaPaciente = firmas.find(f => f.metadata && f.metadata.tipo === 'paciente');
  
  // Si solo hay una firma, usar la función normal
  if (firmas.length === 1) {
    return agregarFirmaComoImagen(doc, firmas[0].imagenBase64, yPosition, firmas[0].metadata || {});
  }
  
  // Configuración de columnas
  const margenIzq = 15;
  const margenDer = 15;
  const anchoUtil = 210 - margenIzq - margenDer; // Ancho carta menos márgenes
  const anchoColumna = anchoUtil / 2;
  const espacioEntreColumnas = 5;
  const alturaCelda = 45; // Altura fija para ambas celdas
  
  // Posiciones X
  const xProfesional = margenIzq;
  const xPaciente = margenIzq + anchoColumna + espacioEntreColumnas;
  
  let yActual = yPosition;
  
  // Dibujar firma del profesional (columna izquierda)
  if (firmaProfesional) {
    // Título
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('FIRMA DEL PROFESIONAL', xProfesional, yActual);
    
    // Línea divisoria superior
    doc.setDrawColor(222, 226, 230);
    doc.setLineWidth(0.5);
    doc.line(xProfesional, yActual + 2, xProfesional + anchoColumna - espacioEntreColumnas, yActual + 2);
    
    // Imagen de firma centrada
    if (firmaProfesional.imagenBase64) {
      try {
        const imgAncho = 45;
        const imgAlto = 22;
        const imgX = xProfesional + (anchoColumna - espacioEntreColumnas - imgAncho) / 2;
        const imgY = yActual + 5;
        doc.addImage(firmaProfesional.imagenBase64, 'PNG', imgX, imgY, imgAncho, imgAlto);
      } catch (error) {
        console.error('Error agregando imagen de firma profesional:', error);
      }
    }
    
    // Metadata debajo de la firma
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(108, 117, 125);
    let yMetadata = yActual + 29;
    
    if (firmaProfesional.metadata.nombre) {
      const nombreLineas = doc.splitTextToSize(firmaProfesional.metadata.nombre, anchoColumna - espacioEntreColumnas - 10);
      doc.text(nombreLineas, xProfesional + 5, yMetadata);
      yMetadata += nombreLineas.length * 2.5;
    }
    
    if (firmaProfesional.metadata.registroMedico) {
      let textoRM = 'RM: ' + firmaProfesional.metadata.registroMedico;
      if (firmaProfesional.metadata.codigoHabilitacion) {
        textoRM += ' | Hab: ' + firmaProfesional.metadata.codigoHabilitacion;
      }
      const rmLineas = doc.splitTextToSize(textoRM, anchoColumna - espacioEntreColumnas - 10);
      doc.text(rmLineas, xProfesional + 5, yMetadata);
    }
  }
  
  // Dibujar firma del paciente (columna derecha)
  if (firmaPaciente) {
    // Título
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('FIRMA DEL PACIENTE', xPaciente, yActual);
    
    // Línea divisoria superior
    doc.setDrawColor(222, 226, 230);
    doc.setLineWidth(0.5);
    doc.line(xPaciente, yActual + 2, xPaciente + anchoColumna - espacioEntreColumnas, yActual + 2);
    
    // Imagen de firma centrada
    if (firmaPaciente.imagenBase64) {
      try {
        const imgAncho = 45;
        const imgAlto = 22;
        const imgX = xPaciente + (anchoColumna - espacioEntreColumnas - imgAncho) / 2;
        const imgY = yActual + 5;
        doc.addImage(firmaPaciente.imagenBase64, 'PNG', imgX, imgY, imgAncho, imgAlto);
      } catch (error) {
        console.error('Error agregando firma paciente:', error);
      }
    }
  }
  
  // 🆕 LÍNEA DIVISORIA INFERIOR CONTINUA - Atraviesa ambas columnas
  doc.setDrawColor(222, 226, 230);
  doc.setLineWidth(0.5);
  doc.line(margenIzq, yActual + alturaCelda + 2, margenIzq + anchoUtil, yActual + alturaCelda + 2);
  
  // Retornar posición Y después de las firmas
  return yActual + alturaCelda + 5;
}
